import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ExternalLink, Lock, Unlock, Users, MessageSquare } from 'lucide-react';
import { API } from '../../App';
import { Button } from '../ui/button';

const STATUS_STYLES = {
  active: 'bg-red-100 text-red-800',
  monitoring: 'bg-amber-100 text-amber-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  archived: 'bg-gray-100 text-gray-600',
};

// Admin panel for Incident Rooms — status changes, lock/unlock, quick access.
export const AdminIncidentsPanel = ({ headers }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('open');
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (filter === 'archived') {
        const r = await axios.get(`${API}/incidents`, { params: { status: 'archived' } });
        setIncidents(r.data.incidents || []);
      } else {
        const r = await axios.get(`${API}/incidents`);
        setIncidents(r.data.incidents || []);
      }
    } catch {
      toast.error('Could not load incidents');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await axios.put(`${API}/incidents/${id}`, { status }, { headers });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };
  const toggleLock = async (id, locked) => {
    try {
      await axios.post(`${API}/incidents/${id}/lock`, { locked }, { headers });
      toast.success(locked ? 'Room locked' : 'Room unlocked');
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="admin-incidents-panel">
      <div className="flex items-center gap-2 mb-4">
        {[{ key: 'open', label: 'Open & resolved' }, { key: 'archived', label: 'Archived' }].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
              filter === f.key ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 border border-gray-200'
            }`}
            data-testid={`admin-inc-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="text-center text-gray-400 py-10">Loading…</div>
      ) : incidents.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500" data-testid="admin-inc-empty">
          Nothing here.
        </div>
      ) : (
        <div className="space-y-3">
          {incidents.map(inc => (
            <div key={inc.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid={`admin-inc-${inc.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_STYLES[inc.status] || STATUS_STYLES.archived}`}>
                  {inc.status}
                </span>
                {inc.severity !== 'normal' && (
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-orange-100 text-orange-800">{inc.severity}</span>
                )}
                {inc.locked && <Lock className="h-3.5 w-3.5 text-amber-600" />}
                <span className="ml-auto text-[11px] text-gray-400">{(inc.createdAt || '').slice(0, 16).replace('T', ' ')}</span>
              </div>
              <p className="font-semibold text-gray-900 text-sm">{inc.title}</p>
              <p className="text-xs text-gray-600 mb-1">{(inc.areasAffected || []).join(', ')}</p>
              <div className="flex items-center gap-3 text-[11px] text-gray-500 mb-2">
                <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {inc.followerCount || 0} followers</span>
                <span className="inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {inc.reportCount || 0} reports · {inc.updateCount || 0} official updates</span>
                <span>by {inc.createdByName}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap border-t border-gray-100 pt-2">
                <select
                  value={inc.status}
                  onChange={e => changeStatus(inc.id, e.target.value)}
                  className="border rounded-md px-2 py-1 text-xs bg-white"
                  data-testid={`admin-inc-status-${inc.id}`}
                >
                  {['active', 'monitoring', 'resolved', 'archived'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <Button size="sm" variant="outline" onClick={() => toggleLock(inc.id, !inc.locked)} data-testid={`admin-inc-lock-${inc.id}`}>
                  {inc.locked ? <Unlock className="h-3.5 w-3.5 mr-1" /> : <Lock className="h-3.5 w-3.5 mr-1" />}
                  {inc.locked ? 'Unlock' : 'Lock'}
                </Button>
                <Button size="sm" variant="ghost" className="ml-auto" onClick={() => navigate(`/incidents/${inc.id}`)} data-testid={`admin-inc-open-${inc.id}`}>
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open room
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminIncidentsPanel;
